import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useRef, useState, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { FILES_BASE_URL } from "../../../api/baseURL";
import multiplefile from "../../../assets/images/add_to_photos.svg";
import uploadedfiles from "../../../assets/images/Upload.svg";
import removefile from "../../../assets/images/Xclose.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import AcceptedFilesNote from "../acceptedFilesNote";
import useScreenResolution from "../../../hooks/useScreenResolution";
import {
  requestToken,
  uploadFile,
} from "../../../redux/features/upload/fileUploadSlice";
import { getClientSource } from "../../../utils/getClientSource";

const FileUploadInput = ({
  name,
  onFileSelect,
  onFileRemove,
  onLoseFocus,
  validTypes = ["image/jpeg", "image/png", "application/pdf"],
  maxSizeKB = 5000,
  uploadToServer = true,
  showRemoveButton = true,
  multiple = false,
  maxFiles = 5,
  className = "",
  disabled = false,
  initialFiles = [], // New prop to initialize with existing files
}) => {
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const screenResolution = useScreenResolution();
  const fileInputRef = useRef(null);

  const [formState, setFormState] = useState(() => {
    // Initialize single file mode with initialFiles if provided
    if (!multiple && initialFiles && initialFiles.length > 0) {
      const firstFile = initialFiles[0];
      return {
        fileName: firstFile.fileName || firstFile.name || "",
        fileSize: firstFile.fileSize || firstFile.size || "", // Don't show "Unknown", just empty string
        file: firstFile.file || null,
        base64File: firstFile.base64 || "",
        error: "",
        uploading: false,
      };
    }
    return {
      fileName: "",
      fileSize: "",
      file: null,
      base64File: "",
      error: "",
      uploading: false,
    };
  });

  // For multiple files mode - initialize with initialFiles prop
  const [uploadedFiles, setUploadedFiles] = useState(() => {
    // Convert initialFiles to the format expected by the component
    if (multiple && initialFiles && initialFiles.length > 0) {
      return initialFiles.map((file, index) => ({
        id: file.id || `restored-${index}`, // Preserve original ID if available
        name: file.fileName || file.name || `file-${index}`,
        size: file.fileSize || file.size || "", // Don't show "Unknown", just empty string
        uploaded: true, // Mark as already uploaded
        uploadResult: file.uploadResult || null,
        fileLink: file.fileLink || null,
        file: file.file || null,
      }));
    }
    return [];
  });
  const [uploadProgress, setUploadProgress] = useState(() => {
    // Initialize progress for restored files to show "✓ Uploaded" status
    if (multiple && initialFiles && initialFiles.length > 0) {
      const progressInit = {};
      initialFiles.forEach((file, index) => {
        const fileId = file.id || `restored-${index}`;
        progressInit[fileId] = 100;
      });
      return progressInit;
    }
    return {};
  });

  // Create a stable reference for initialFiles by serializing IDs
  const initialFilesKey = useMemo(() => {
    if (!initialFiles || initialFiles.length === 0) return 'empty';
    return initialFiles.map(f => f.id || f.fileName || f.name).join(',');
  }, [initialFiles]);

  // Track the previous key to detect real changes
  const prevInitialFilesKeyRef = useRef(initialFilesKey);

  // Update internal state when initialFiles prop changes (for persistence restoration)
  useEffect(() => {
    // Only update if initialFiles actually changed (not just a new reference)
    if (prevInitialFilesKeyRef.current === initialFilesKey) {
      return; // No actual change, skip update
    }

    prevInitialFilesKeyRef.current = initialFilesKey;

    if (initialFiles && initialFiles.length > 0) {
      if (multiple) {
        // Convert initialFiles to internal format
        const restoredFiles = initialFiles.map((file, index) => ({
          id: file.id || `restored-${index}`, // Preserve original ID if available
          name: file.fileName || file.name || `file-${index}`,
          size: file.fileSize || file.size || "", // Don't show "Unknown", just empty string
          uploaded: true,
          uploadResult: file.uploadResult || null,
          fileLink: file.fileLink || null,
          file: file.file || null,
        }));

        setUploadedFiles(restoredFiles);

        // Set upload progress to 100% for all restored files to show "✓ Uploaded" status
        const progressUpdate = {};
        restoredFiles.forEach((file) => {
          progressUpdate[file.id] = 100;
        });
        setUploadProgress(progressUpdate);
      } else{
        // Update formState for single file mode
        const firstFile = initialFiles[0];
        setFormState((prev) => ({
          ...prev,
          fileName: firstFile.fileName || firstFile.name || "",
          fileSize: firstFile.fileSize || firstFile.size || "", // Don't show "Unknown", just empty string
          file: firstFile.file || null,
          base64File: firstFile.base64 || "",
        }));
      }
    } else if (initialFiles && initialFiles.length === 0) {
      // Clear files when initialFiles is empty
      if (multiple) {
        setUploadedFiles([]);
        setUploadProgress({});
      } else {
        setFormState((prev) => ({
          ...prev,
          fileName: "",
          fileSize: "",
          file: null,
          base64File: "",
        }));
      }
    }
  }, [initialFilesKey, multiple]);

  const RequestLinkAPI = async (fileId) => {
    const url = FILES_BASE_URL + "/links/request";
    const data = {
      file_id: fileId,
      ip: "",
      validity: 7200,
      reference: "customer portal",
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  };

  const generateUniqueId = () => {
    return (
      Date.now().toString(36) + "-" + Math.random().toString(36).substring(2, 8)
    );
  };

  const processFiles = async (files) => {
    const fileList = Array.isArray(files) ? files : [files];

    if (multiple) {
      // Multiple files mode
      const currentFileCount = uploadedFiles.length;
      const newFileCount = fileList.length;

      if (currentFileCount + newFileCount > maxFiles) {
        setFormState((prev) => ({
          ...prev,
          error: translate(
            maxFiles === 1
              ? `You can only upload up to 1 file.`
              : `You can only upload up to ${maxFiles} files.`,
            maxFiles === 1
              ? `يمكنك تحميل ملف واحد كحد أقصى.`
              : `يمكنك تحميل حتى ${maxFiles} ملفات كحد أقصى.`
          ),
        }));
        return;
      }

      // Process each file
      fileList.forEach((file) => {
        if (!file) return;

        // Validate file type
        if (!validTypes.includes(file.type)) {
          setFormState((prev) => ({
            ...prev,
            error: translate(
              `Please upload a valid file format (${validTypes.join(", ")}).`,
              `يرجى تحميل ملف بصيغة صالحة (${validTypes.join(", ")}).`
            ),
          }));
          return;
        }

        // Validate file size
        const fileSizeKB = file.size / 1024;
        if (fileSizeKB > maxSizeKB) {
          const formattedSize = maxSizeKB.toLocaleString("fr-FR");
          setFormState((prev) => ({
            ...prev,
            error: translate(
              `File size must be less than ${formattedSize} KB.`,
              `يجب أن يكون حجم الملف أقل من ${formattedSize} كيلوبايت.`
            ),
          }));
          return;
        }

        const fileId = generateUniqueId();
        const fileObj = {
          id: fileId,
          file,
          name: file.name,
          size: fileSizeKB.toFixed(2) + " KB",
          uploaded: false,
        };

        // Clear any previous errors when adding a valid file
        setFormState((prev) => ({
          ...prev,
          error: "",
        }));

        // Add to uploaded files list
        setUploadedFiles((prev) => [...prev, fileObj]);

        // Start upload process
        uploadSingleFile(fileObj);
      });
    } else {
      // Single file mode
      const file = fileList[0];
      if (!file) return;

      // Validate file type
      if (!validTypes.includes(file.type)) {
        setFormState((prev) => ({
          ...prev,
          error: translate(
            `Please upload a valid file format (${validTypes.join(", ")}).`,
            `يرجى تحميل ملف بصيغة صالحة (${validTypes.join(", ")}).`
          ),
        }));
        return;
      }

      // Validate file size
      const fileSizeKB = file.size / 1024;
      if (fileSizeKB > maxSizeKB) {
        const formattedSize = maxSizeKB.toLocaleString("fr-FR");
        setFormState((prev) => ({
          ...prev,
          error: translate(
            `File size must be less than ${formattedSize} KB.`,
            `يجب أن يكون حجم الملف أقل من ${formattedSize} كيلوبايت.`
          ),
        }));
        return;
      }

      setFormState((prev) => ({ ...prev, uploading: true, error: "" }));
      uploadSingleFile({ file, name: file.name });
    }
  };

  const uploadSingleFile = async (fileObj) => {
    const file = fileObj.file;
    const fileId = fileObj.id;

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result.split(",")[1];

        let uploadResult = null;
        let fileLink = null;

        if (uploadToServer) {
          try {
            // Set progress for multiple files
            if (multiple && fileId) {
              setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));
            }

            // Request Token
            const tokenPayload = {
              secret: "hello123",
              agent: getClientSource(),
              item_type: name,
              item_id: name + "_" + file.name,
              source: getClientSource(),
              description: "Files token request from customer portal.",
            };

            const tokenResponse = dispatch(requestToken(tokenPayload));

            // Update progress
            if (multiple && fileId) {
              setUploadProgress((prev) => ({ ...prev, [fileId]: 50 }));
            }

            // Upload File
            const uploadPayload = {
              token: (await tokenResponse).payload,
              source: getClientSource(),
              file_name: name + "_" + file.name,
              file_base64: base64String,
            };

            const uploadAction = dispatch(uploadFile(uploadPayload));
            uploadResult = await unwrapResult(await uploadAction);

            if (uploadResult?.data?.file_id) {
              const linkResponse = await RequestLinkAPI(
                uploadResult.data.file_id
              );
              fileLink = linkResponse?.data?.link;
            }

            // Complete progress
            if (multiple && fileId) {
              setUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));
              setUploadedFiles((prev) =>
                prev.map((f) =>
                  f.id === fileId
                    ? { ...f, uploaded: true, uploadResult, fileLink }
                    : f
                )
              );
            }
          } catch (uploadError) {
            console.error("Upload failed:", uploadError);
            if (multiple && fileId) {
              setUploadProgress((prev) => ({ ...prev, [fileId]: -1 })); // Error state
            }
            throw new Error(
              translate(
                "Failed to upload file. Please try again.",
                "فشل في تحميل الملف. يرجى المحاولة مرة أخرى."
              )
            );
          }
        } else {
          // No server upload, just mark as complete
          if (multiple && fileId) {
            setUploadProgress((prev) => ({ ...prev, [fileId]: 100 }));
            setUploadedFiles((prev) =>
              prev.map((f) => (f.id === fileId ? { ...f, uploaded: true } : f))
            );
          }
        }

        if (multiple) {
          // Call parent callback for each file
          if (onFileSelect) {
            onFileSelect({
              id: fileId, // Include the file ID for tracking
              file,
              fileName: file.name,
              fileSize: (file.size / 1024).toFixed(2) + " KB",
              base64: base64String,
              uploadResult,
              fileLink,
            });
          }
        } else {
          // Single file mode
          const fileSizeKB = file.size / 1024;
          const newFormState = {
            fileName: file.name,
            fileSize: fileSizeKB.toFixed(2) + " KB",
            file: file,
            base64File: base64String,
            error: "",
            uploading: false,
          };

          setFormState(newFormState);

          // Call parent callback with file details
          if (onFileSelect) {
            onFileSelect({
              file,
              fileName: file.name,
              fileSize: fileSizeKB.toFixed(2) + " KB",
              base64: base64String,
              uploadResult,
              fileLink,
            });
          }
        }
      };

      reader.onerror = () => {
        if (multiple && fileId) {
          setUploadProgress((prev) => ({ ...prev, [fileId]: -1 })); // Error state
        } else {
          setFormState((prev) => ({
            ...prev,
            error: translate(
              "Failed to read file. Please try again.",
              "فشل في قراءة الملف. يرجى المحاولة مرة أخرى."
            ),
            uploading: false,
          }));
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing file:", error);
      if (multiple && fileId) {
        setUploadProgress((prev) => ({ ...prev, [fileId]: -1 })); // Error state
      } else {
        setFormState((prev) => ({
          ...prev,
          error:
            error.message ||
            translate(
              "Failed to process file. Please try again.",
              "فشل في معالجة الملف. يرجى المحاولة مرة أخرى."
            ),
          uploading: false,
        }));
      }
    }
  };

  const handleFileChange = async (event) => {
    const files = multiple
      ? Array.from(event.target.files)
      : [event.target.files[0]];
    processFiles(files);
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processFiles(acceptedFiles);
    }
  };

  const removeFile = (fileId) => {
    if (multiple) {
      setUploadedFiles((prev) => {
        const updatedFiles = prev.filter((f) => f.id !== fileId);

        // Notify parent component with the file ID being removed
        if (onFileRemove) {
          onFileRemove(fileId);
        }

        return updatedFiles;
      });
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });

      // Clear error when files are removed (in case it was a max files error)
      setFormState((prev) => ({
        ...prev,
        error: "",
      }));
    } else {
      // Single file mode
      const resetState = {
        fileName: "",
        fileSize: "",
        file: null,
        base64File: "",
        error: "",
        uploading: false,
      };

      setFormState(resetState);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      if (onFileRemove) {
        onFileRemove();
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple,
    accept: validTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    disabled:
      disabled ||
      (multiple ? uploadedFiles.length >= maxFiles : formState.file != null),
  });

  // Handle blur event for dropzone
  const handleBlur = (event) => {
    // Check if the new focus target is outside the dropzone
    if (
      onLoseFocus &&
      (!event.relatedTarget ||
        !event.currentTarget.contains(event.relatedTarget))
    ) {
      onLoseFocus();
    }
  };

  return (
    <div className={`flex flex-col space-y-2 ${className}`}>
      {/* Drop Zone - Always visible for multiple files, only when no file for single */}
      {!screenResolution.mobile ? (
        (multiple || formState.file == null) && (
          <div
            {...getRootProps({
              // min-h, not h: the accepted-formats line has to be able to
              // wrap without being clipped by a fixed height.
              className: `dropzone min-h-[164px] justify-center items-center bg-[#F6F8F9] px-4 py-8 rounded-md text-center border-2 border-dashed border-[#DDE2E4] hover:border-[#55C6EF] transition-colors ${
                disabled ||
                (multiple
                  ? uploadedFiles.length >= maxFiles
                  : formState.uploading)
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`,
              onBlur: handleBlur,
              tabIndex: 0, // Make it focusable
            })}
          >
            <input {...getInputProps()} />
            <p className="justify-center flex mb-2">
              <img src={uploadedfiles} className="items-center" alt="upload" />
            </p>
            <p className="text-[#252C32] text-center text-sm not-italic font-normal leading-6 mb-2">
              {multiple
                ? translate(
                    uploadedFiles.length >= maxFiles
                      ? `Maximum ${maxFiles} files reached`
                      : "Drag and drop files here",
                    uploadedFiles.length >= maxFiles
                      ? `تم الوصول للحد الأقصى ${maxFiles} ملفات`
                      : "اسحب الملفات هنا"
                  )
                : formState.uploading
                ? translate("Uploading...", "جاري التحميل...")
                : translate("Drag and drop files here", "اسحب الملفات هنا")}
            </p>
            <button
              type="button"
              className="items-center gap-2.5 border bg-white px-4 py-2 rounded-md text-[#48535B] text-center text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
              disabled={
                disabled ||
                (multiple
                  ? uploadedFiles.length >= maxFiles
                  : formState.uploading)
              }
            >
              {multiple
                ? translate(
                    uploadedFiles.length >= maxFiles
                      ? "Maximum reached"
                      : "Browse",
                    uploadedFiles.length >= maxFiles
                      ? "تم الوصول للحد الأقصى"
                      : "تصفح"
                  )
                : formState.uploading
                ? translate("Uploading...", "جاري التحميل...")
                : translate("Browse", "تصفح")}
            </button>
            <AcceptedFilesNote types={validTypes} maxSizeMb={maxSizeKB / 1000} />
          </div>
        )
      ) : (
        /* Mobile View */
        <div className="flex flex-col space-y-2">
          <div className="relative">
            <div
              className={`flex fileInput w-full min-h-[164px] mb-4 bg-[#F6F8F9] rounded-md max767:mb-0${
                disabled ? "opacity-50" : ""
              }`}
            >
              <input
                type="file"
                className="opacity-0 flex h-[164px] justify-center items-center shrink-0 self-stretch w-full rounded-md z-[99] absolute"
                onChange={handleFileChange}
                onBlur={handleBlur}
                ref={fileInputRef}
                disabled={
                  disabled ||
                  (multiple
                    ? uploadedFiles.length >= maxFiles
                    : formState.uploading)
                }
                accept={validTypes.join(",")}
                multiple={multiple}
              />
              <div className="flex flex-col items-center gap-1 relative z-0 mx-auto my-0 top-[60px] inset-x-0">
                <p>
                  <img src={multiplefile} alt="Select file" />
                </p>
                <p>
                  <label>
                    {multiple
                      ? uploadedFiles.length >= maxFiles
                        ? translate(
                            "Maximum files reached",
                            "تم الوصول للحد الأقصى من الملفات"
                          )
                        : translate("Add files or photos", "إضافة ملفات أو صور")
                      : formState.uploading
                      ? translate("Uploading...", "جاري التحميل...")
                      : translate("Add file or photo", "إضافة ملف أو صورة")}
                  </label>
                </p>
                <AcceptedFilesNote
                  types={validTypes}
                  maxSizeMb={maxSizeKB / 1000}
                  className="mt-0 px-4"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single File Display (Non-multiple mode only) */}
      {!multiple && formState.fileName && !screenResolution.mobile && (
        <div className="flex items-center w-full bg-[#F6F8F9] rounded-md p-4">
          <span className="flex-1 text-[#252C32] text-sm font-normal">
            {formState.fileName}
            {formState.fileSize ? ` (${formState.fileSize})` : ""}
          </span>
          {showRemoveButton && (
            <button
              onClick={() => removeFile()}
              className="ml-2 p-1 hover:bg-[#E5E9EB] rounded"
              disabled={disabled || formState.uploading}
            >
              <img src={removefile} alt="Remove file" className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Single File Display Mobile (Non-multiple mode only) */}
      {!multiple && formState.fileName && screenResolution.mobile && (
        <div className="mt-4 flex">
          <li className="flex justify-between flex-1 gap-2 bg-[#F6F8F9] pl-4 pr-2 py-2 rounded-md text-[#252C32] text-sm not-italic font-normal leading-6 mb-2 items-center">
            <span>{formState.fileName}</span>
            {showRemoveButton && (
              <button
                onClick={() => removeFile()}
                style={{ marginLeft: "10px" }}
                disabled={disabled || formState.uploading}
              >
                <img src={removefile} alt="Remove file" />
              </button>
            )}
          </li>
        </div>
      )}

      {/* Multiple Files Display */}
      {multiple && uploadedFiles.length > 0 && (
        <div className="uploaded-files mt-4 flex flex-col gap-2">
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center w-full bg-[#F6F8F9] rounded-md p-4"
            >
              <div className="flex-1 min-w-0 mr-2">
                <span className="text-[#252C32] text-sm font-normal break-words block">
                  {file.name}
                  {file.size ? ` (${file.size})` : ""}
                </span>
              </div>

              {/* Progress Bar */}
              {uploadProgress[file.id] !== undefined &&
                uploadProgress[file.id] < 100 &&
                uploadProgress[file.id] >= 0 && (
                  <div className="w-24 mx-4">
                    <div className="relative h-2 w-full bg-[#AAE2F7] rounded">
                      <div
                        className="absolute top-0 h-full bg-[#00A9E7] rounded"
                        style={{ width: `${uploadProgress[file.id]}%` }}
                      ></div>
                    </div>
                  </div>
                )}

              {/* Status Indicator */}
              {uploadProgress[file.id] === 100 && (
                <span className="text-green-500 text-xs mr-2 flex-shrink-0">
                  ✓ {translate("Uploaded", "تم التحميل")}
                </span>
              )}

              {uploadProgress[file.id] === -1 && (
                <span className="text-red-500 text-xs mr-2 flex-shrink-0">
                  ✗ {translate("Failed", "فشل")}
                </span>
              )}

              {/* Remove Button - Always show when showRemoveButton is true */}
              {showRemoveButton && (
                <button
                  onClick={() => removeFile(file.id)}
                  className="ml-2 p-1 hover:bg-[#E5E9EB] rounded flex-shrink-0"
                  disabled={disabled}
                  title={translate("Remove file", "حذف الملف")}
                >
                  <img src={removefile} alt="Remove file" className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {formState.error && (
        <p className="text-red-500 text-sm">{formState.error}</p>
      )}

      {formState.uploading && (
        <p className="text-blue-500 text-sm">
          {translate("Uploading file...", "جاري تحميل الملف...")}
        </p>
      )}
    </div>
  );
};

export default FileUploadInput;
